import { mdiArrowRight, mdiEmailOutline, mdiOpenInNew } from '@mdi/js'
import kebabCase from 'lodash.kebabcase'
import { toValue } from 'vue'
import {
  MENU_BAR_HEIGHT,
  FILTER_TYPE_ARTICLE,
  FILTER_TYPE_CASE_STUDY,
  FILTER_TYPE_DOWNLOAD,
  FILTER_TYPE_VIDEO
} from './consts'

/**
 * @typedef {Object} Slugifiable
 * @property {Object} [item]
 * @property {Object} [resource]
 * @property {string} [page_slug]
 * @property {string} [slug]
 * @property {string} [tag]
 * @property {string} [title]
 * @property {string|number} [id]
 * @property {string} [name]
 */

/**
 * @typedef {import('#root/types/resources').ResourceLinkInput} ResourceItem
 */

/**
 * @typedef {Object} FileValue
 * @property {string} [url]
 */

/**
 * @typedef {Object} ScrollToOptions
 * @property {'auto'|'smooth'|'instant'} [behavior]
 * @property {number} [height]
 * @property {number} [delay]
 */

const USD = new Intl.NumberFormat('en-US', {
  currency: 'USD',
  maximumFractionDigits: 2,
  minimumFractionDigits: 0,
  style: 'currency'
})

/** @type {Record<string, Intl.NumberFormat>} */
const priceFormatters = {
  USD
}

/**
 * Build href for an article (list or single).
 * @param {Slugifiable} value - Article or slugifiable item.
 * @param {boolean} [all] - If true, link to articles filter; otherwise single article path.
 * @returns {string}
 */
export function articleHref(value, all) {
  return all
    ? `/resources?type=${FILTER_TYPE_ARTICLE}`
    : `/articles/${slugify(value)}`
}

/**
 * Build href for a case study (list or single).
 * @param {Slugifiable} value - Case study or slugifiable item.
 * @param {boolean} [all] - If true, link to case studies filter; otherwise single case study path.
 * @returns {string}
 */
export function caseStudyHref(value, all) {
  return all
    ? `/resources?type=${FILTER_TYPE_CASE_STUDY}`
    : `/case-studies/${slugify(value)}`
}

/**
 * Build href for a download (list or single).
 * @param {Slugifiable} value - Download or slugifiable item.
 * @param {boolean} [all] - If true, link to downloads filter; otherwise single download path.
 * @returns {string}
 */
export function downloadHref(value, all) {
  return all
    ? `/resources?type=${FILTER_TYPE_DOWNLOAD}`
    : `/downloads/${slugify(value)}`
}

/**
 * Resolve file URL, swapping upload origin for CDN when present.
 * @param {FileValue|null|undefined} value - File object with optional url.
 * @returns {string|undefined}
 */
export function fileHref(value) {
  if (value && value.url)
    return value.url.replace(
      import.meta.env.VITE_UPLOADS_ORIGIN,
      import.meta.env.VITE_UPLOADS_CDN
    )
}

/**
 * Build href for a video (list or single).
 * @param {Slugifiable} value - Video or slugifiable item.
 * @param {boolean} [all] - If true, link to videos filter; otherwise single video path.
 * @returns {string}
 */
export function videoHref(value, all) {
  return all
    ? `/resources?type=${FILTER_TYPE_VIDEO}`
    : `/videos/${slugify(value)}`
}

/**
 * @param {ResourceItem} value
 * @returns {boolean}
 */
export function isArticle(value) {
  return (
    value.component === 'Article' || value.__component === 'content.ArticleItem'
  )
}

/**
 * @param {ResourceItem} value
 * @returns {boolean}
 */
export function isCaseStudy(value) {
  const item = value.item || value
  return (
    value.component === 'CaseStudy' ||
    value.__component === 'content.CaseStudyItem' ||
    Boolean(
      item.resource_types &&
      item.resource_types.some(({ id }) => id === FILTER_TYPE_CASE_STUDY)
    )
  )
}

/**
 * @param {ResourceItem} value
 * @returns {boolean}
 */
export function isDownload(value) {
  return (
    value.component === 'Download' ||
    value.__component === 'content.DownloadItem'
  )
}

/**
 * @param {ResourceItem} value
 * @returns {boolean}
 */
export function isFaq(value) {
  return value.component === 'Faq' || value.__component === 'content.FaqItem'
}

/**
 * @param {ResourceItem} value
 * @returns {boolean}
 */
export function isVideo(value) {
  return (
    value.component === 'Video' || value.__component === 'content.VideoItem'
  )
}

/**
 * Apply link styles to agreement HTML body.
 * @param {string} [value] - Raw HTML string.
 * @returns {string}
 */
export function agreementBody(value) {
  let body = value || ''
  //
  // Apply styles and adjust heading levels
  // Pretty safe since html is generated by markdown-it
  //
  body = body.replaceAll('<a href="/', '<a class="text-secondary" href="/')
  body = body.replaceAll(
    '<a href="',
    '<a class="text-secondary" target="_blank" href="'
  )
  return body
}

/**
 * Apply intro styles and heading levels to HTML body.
 * @param {string} [value] - Raw HTML string.
 * @returns {string}
 */
export function introBody(value) {
  let body = value || ''
  //
  // Apply styles and adjust heading levels
  // Pretty safe since html is generated by markdown-it
  //
  body = body.replaceAll('<img src="', '<img class="img-responsive" src="')
  body = body.replaceAll('<a href="/', '<a class="text-secondary" href="/')
  body = body.replaceAll(
    '<a href="',
    '<a class="text-secondary" target="_blank" href="'
  )
  body = body.replaceAll('<p>', '<p class="text-body-1 mb-3">')
  body = body.replaceAll('<ol>', '<ol class="text-body-1 mb-3 ml-6">')
  body = body.replaceAll('<ul>', '<ul class="text-body-1 mb-3 ml-6">')
  body = body.replaceAll(
    '<blockquote>',
    '<blockquote class="blockquote mb-3 mx-6">'
  )
  body = body.replaceAll('<h6>', '<h6 class="text-subtitle-2 mb-3 mt-4">')
  body = body.replaceAll('<h5>', '<h6 class="text-subtitle-1 mb-3 mt-4">')
  body = body.replaceAll('</h5>', '</h6>')
  body = body.replaceAll('<h4>', '<h5 class="text-h6 mb-3 mt-4">')
  body = body.replaceAll('</h4>', '</h5>')
  body = body.replaceAll('<h3>', '<h4 class="text-h6 mb-3 mt-4">')
  body = body.replaceAll('</h3>', '</h4>')
  body = body.replaceAll('<h2>', '<h3 class="text-h5 mb-3 mt-4">')
  body = body.replaceAll('</h2>', '</h3>')
  body = body.replaceAll('<h1>', '<h2 class="text-h4 mb-3 mt-2">')
  body = body.replaceAll('</h1>', '</h2>')
  body = body.replaceAll('<hr>', '<hr class="my-6">')

  return body
}

/**
 * Apply resource page styles to HTML body.
 * @param {string} [value] - Raw HTML string.
 * @returns {string}
 */
export function resourceBody(value) {
  let body = value || ''
  //
  // Apply styles
  // Pretty safe since html is generated by markdown-it
  //
  body = body.replaceAll('<img src="', '<img class="img-responsive" src="')
  body = body.replaceAll('<a href="/', '<a class="text-secondary" href="/')
  body = body.replaceAll(
    '<a href="',
    '<a class="text-secondary" target="_blank" href="'
  )
  body = body.replaceAll('<p>', '<p class="text-body-0 mb-4">')
  body = body.replaceAll(
    '<table>',
    '<div class="overflow-auto my-8"><table class="text-body-0">'
  )
  body = body.replaceAll('</table>', '</table></div>')
  body = body.replaceAll('<th>', '<th class="pa-2 bg-surface-light">')
  body = body.replaceAll('<td>', '<td class="pa-2 border-b">')
  body = body.replaceAll('<ol>', '<ol class="text-body-0 mb-4 ml-6">')
  body = body.replaceAll('<ul>', '<ul class="text-body-0 mb-4 ml-6">')
  body = body.replaceAll('<blockquote>', '<blockquote class="blockquote mx-6">')
  body = body.replaceAll('<h6>', '<h6 class="text-subtitle-2 mb-4 mt-5">')
  body = body.replaceAll('<h5>', '<h5 class="text-subtitle-1 mb-4 mt-5">')
  body = body.replaceAll('<h4>', '<h4 class="text-h6 mb-4 mt-6">')
  body = body.replaceAll('<h3>', '<h3 class="text-h5 mb-4 mt-6">')
  body = body.replaceAll('<h2>', '<h2 class="text-h4 mb-4 mt-8">')
  body = body.replaceAll('<h1>', '<h1 class="text-h3 mb-4 mt-4">')

  return body
}

/**
 * Build href for a generic resource (article, case study, download, FAQ, or video).
 * @param {ResourceItem} value - Resource item.
 * @param {boolean} [all] - If true, link to filtered list; otherwise single resource path.
 * @returns {string}
 */
export function resourceHref(value, all) {
  let href = '/'
  if (isArticle(value)) href = articleHref(value, all)
  else if (isCaseStudy(value)) href = caseStudyHref(value, all)
  else if (isDownload(value)) href = downloadHref(value, all)
  else if (isFaq(value)) href = '/faqs'
  else if (isVideo(value)) href = videoHref(value, all)
  return href
}

/**
 * Get human-readable resource type name.
 * @param {ResourceItem} value - Resource item.
 * @param {boolean} [plural] - If true, return plural form.
 * @returns {string}
 */
export function resourceType(value, plural) {
  let name = ''
  if (isArticle(value)) name = plural ? 'Articles' : 'Article'
  else if (isCaseStudy(value)) name = plural ? 'Case Studies' : 'Case Study'
  else if (isDownload(value)) name = plural ? 'Downloads' : 'Download'
  else if (isFaq(value)) name = plural ? 'FAQ’s' : 'FAQ'
  else if (isVideo(value)) name = plural ? 'Videos' : 'Video'
  return name
}

/**
 * Extract numeric id from a slug string (e.g. "my-title-123" -> 123).
 * @param {string|*} slug - Slug string or any value (non-string returns 0).
 * @returns {number}
 */
export function slugId(slug) {
  if (typeof slug === 'string') {
    const parts = slug.split('-')
    const last = parts[parts.length - 1]
    return (last !== undefined ? parseInt(last, 10) : NaN) || 0
  }
  return 0
}

/**
 * Derive URL slug from various object shapes (item, resource, page_slug, slug, tag, title/id, name).
 * @param {Slugifiable|null|undefined} value - Value that may have slug, title, name, etc.
 * @returns {string|undefined}
 */
export function slugify(value) {
  if (value) {
    if (value.item) return slugify(value.item)
    if (value.resource) return slugify(value.resource)
    if (value.page_slug) return value.page_slug
    if (value.slug) return value.slug
    if (value.tag) return kebabCase(value.tag)
    if (value.title && value.id) return `${kebabCase(value.title)}-${value.id}`
    if (value.title) return kebabCase(value.title)
    if (value.name) return kebabCase(value.name)
  }
}

/**
 * Choose icon for an href (external, mailto, or local arrow).
 * @param {string} [value] - URL or path.
 * @param {boolean} [local=true] - If true, use arrow for local links; otherwise undefined for local.
 * @returns {string|undefined} - MDI path or undefined.
 */
export function hrefIcon(value, local = true) {
  if (typeof value === 'string') {
    if (value.startsWith('https:')) return mdiOpenInNew
    if (value.startsWith('mailto:')) return mdiEmailOutline
  }
  return local ? mdiArrowRight : undefined
}

/**
 * Target for links: undefined (same window) for same-origin path, '_blank' otherwise.
 * @param {string} [value] - URL or path.
 * @returns {undefined|'_blank'}
 */
export function hrefTarget(value) {
  return typeof value === 'string' && value.startsWith('/')
    ? undefined
    : '_blank'
}

/**
 * Build mailto URL with optional subject and body.
 * @param {Object} opts
 * @param {string} [opts.body]
 * @param {string} [opts.email]
 * @param {string} [opts.subject]
 * @returns {string}
 */
export function mailToHref({
  body,
  email = import.meta.env.VITE_EMAIL,
  subject = 'Help'
}) {
  const parts = []
  if (subject) parts.push(`subject=${encodeURIComponent(subject)}`)
  if (body) parts.push(`body=${encodeURIComponent(body)}`)
  return `mailto:${email}?${parts.join('&')}`
}

/**
 * Build tel: URL from phone string (digits and + only).
 * @param {Object} opts
 * @param {string} [opts.phone]
 * @returns {string}
 */
export function telHref({ phone }) {
  return phone ? `tel:${phone.replace(/[^0-9+]/g, '')}` : ''
}

/**
 * Format price (cents to display) or return default; supports legacy string pass-through.
 * @deprecated
 * @param {number|string|null|undefined} value - Amount in cents or legacy string.
 * @param {string} [defaultValue='']
 * @param {string} [currency='USD']
 * @returns {string|number}
 */
export function price(value, defaultValue = '', currency = 'USD') {
  return value === undefined || value === null
    ? defaultValue
    : typeof value !== 'number'
      ? value
      : priceFormatters[currency]
        ? priceFormatters[currency].format(value / 100)
        : value
}

/**
 * Format value as USD; accepts number or string, returns default for null/undefined.
 * @param {number|string|null|undefined} value
 * @param {string} [defaultValue='']
 * @returns {string}
 */
export function priceFmt(value, defaultValue = '') {
  return value === undefined || value === null
    ? defaultValue
    : typeof value === 'string'
      ? USD.format(parseFloat(value))
      : typeof value === 'number'
        ? USD.format(value)
        : value
}

/**
 * Scroll to a ref's element, offset by app bar and optional height.
 * Parameter is normalized with Vue's toValue (ref/getter/plain value).
 * @param {import("vue").Ref<HTMLElement | { $el?: HTMLElement } | null>} value - Vue ref to an element or component.
 * @param {ScrollToOptions} [options]
 */
export function scrollToRef(value, options = {}) {
  /** @type {ScrollToOptions} */
  const opts = { behavior: 'smooth', height: 0, ...options }
  const v = toValue(value)
  const el = v && typeof v === 'object' && '$el' in v && v.$el ? v.$el : v
  const top =
    el && el instanceof HTMLElement
      ? el.offsetTop - MENU_BAR_HEIGHT - (opts.height ?? 0)
      : 0
  window.scrollTo({ top, left: 0, behavior: opts.behavior })
}

/**
 * Scroll to a ref in a refs object by key (hash string or slugifiable); optional delay.
 * @param {Object<string, *>} [refs] - Map of key to ref.
 * @param {string|Slugifiable} [key] - Key or hash (e.g. "#section") or slugifiable for slug.
 * @param {ScrollToOptions} [options]
 * @returns {Promise<void>}
 */
export async function scrollToRefKey(
  refs,
  key = window.location.hash,
  options = {}
) {
  const k =
    typeof key === 'string' && key.startsWith('#')
      ? key.substring(1)
      : typeof key === 'string'
        ? key
        : slugify(key)
  if (refs && typeof k === 'string') {
    const ref = refs[k]
    if (ref) {
      if (options.delay) await new Promise(resolve => setTimeout(resolve, 500))
      scrollToRef(ref, options)
    }
  }
}

/**
 * Convert integer cents to float (e.g. 12345 -> 123.45). Returns NaN for non-integer numbers.
 * @param {number} value - Amount in cents (integer).
 * @returns {number}
 */
export function floatCents(value) {
  if (typeof value !== 'number') return NaN

  const str = value.toString().padStart(15, '0')
  if (str.includes('.')) return NaN

  return parseFloat(`${str.slice(0, -2)}.${str.slice(-2)}`)
}

/**
 * Parse decimal string to integer cents (e.g. "123.45" -> 12345). Invalid input returns NaN.
 * @param {string} value - Decimal string.
 * @returns {number}
 */
export function parseCents(value) {
  if (typeof value !== 'string') return NaN

  const parts = value.split('.')
  if (parts.length > 2) return NaN

  const second = parts[1]
  return parts.length > 1 && second !== undefined
    ? parseInt(`${parts[0]}${second.substring(0, 2).padEnd(2, '0')}`)
    : parseInt(`${parts[0]}00`)
}
